// Third-party
import React from 'react';
import debounce from 'lodash/debounce';
import { connect } from 'react-redux';

// Utils
import { formatMessage } from '../../../utils/i18n';
import { CONTACT_LAYOUT } from '../../../utils/contacts';
import { getTableSortedColumns } from '@/utils/data-table';

// Components
import { CancelButton } from '../buttons';
import { ContactsTable } from '../tables';
import Modal from '@/views/components/modals/AccessibleModal';

// Types
import { SortingField } from '../../../types/data';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import { getMyContacts, GET_CONTACTS, setPersistenceSource } from '../../../redux/modules/contacts';
import { selectIsLoadingEvery, startLoaderAction } from '../../../redux/modules/ui/loaders';
import GenericContact from '@/types/generic-contact';
import User from '@/types/user';
import { ContactsTableFields } from '../tables/ContactsTable';
import { ColumnSort } from '@tanstack/react-table';
import { setIsActiveContactPostMessage } from '@/redux/modules/post-message';
import { SEARCH_DEBOUNCE_TIME } from '@/utils/debounce/constants';

type StateProps = {
  contacts: GenericContact[];
  nextContactsToken?: string;
  loading: boolean;
  persistenceSource?: string;
  isPostMessageActive: boolean;
};

type DispatchProps = {
  getMyContacts: (...args: Parameters<typeof getMyContacts>) => Promise<GenericContact[]>;
  startLoaderAction: (name: string) => void;
  setIsActiveContactPostMessage: (isActive: boolean) => void;
  setPersistenceSource: (user: User, key: string, value?: string) => void;
};

type Props = StateProps &
  DispatchProps & {
    show: boolean;
    onCancel: () => void;
    onSelect: (contact: GenericContact) => void;
    user: User;
    title?: string;
    layout?: CONTACT_LAYOUT;
  };

type State = {
  contactsSearchQuery: string;
  contactsSortingFields: SortingField[];
  sortedColumns: ColumnSort[];
};

class SelectContactModal extends React.Component<Props, State> {
  readonly state: State = {
    contactsSearchQuery: '',
    contactsSortingFields: [],
    sortedColumns: [],
  };

  debouncedSearchContacts = debounce(() => {
    const { contactsSearchQuery, contactsSortingFields } = this.state;
    this.props.getMyContacts({ query: contactsSearchQuery, nextToken: undefined, sort: contactsSortingFields });
  }, SEARCH_DEBOUNCE_TIME);

  componentDidMount() {
    this.props.getMyContacts();
  }

  onContactsSortingChange = (contactsSortingFields: SortingField[]) => {
    const { contactsSearchQuery } = this.state;

    this.setState({ contactsSortingFields });
    this.props.getMyContacts({ query: contactsSearchQuery, nextToken: undefined, sort: contactsSortingFields });
  };

  onContactsSearchChange = (contactsSearchQuery: string) => {
    this.setState({ contactsSearchQuery });
    this.props.startLoaderAction(GET_CONTACTS.type);

    this.debouncedSearchContacts();
  };

  onLoadMoreContacts = () => {
    const { contactsSearchQuery, contactsSortingFields } = this.state;
    const { nextContactsToken } = this.props;

    this.props.getMyContacts({ query: contactsSearchQuery, nextToken: nextContactsToken, sort: contactsSortingFields });
  };

  handleSorting = (sort: ColumnSort) => {
    const { contactsSearchQuery } = this.state;
    const { serverSortingParams, columnSort } = getTableSortedColumns(this.state.contactsSortingFields, sort);
    this.setState({ contactsSortingFields: serverSortingParams, sortedColumns: columnSort });
    this.props.getMyContacts({ query: contactsSearchQuery, nextToken: undefined, sort: serverSortingParams });
  };

  render() {
    const {
      contacts,
      show,
      user,
      layout,
      nextContactsToken,
      loading,
      persistenceSource,
      isPostMessageActive,
      onCancel,
      onSelect,
      setIsActiveContactPostMessage,
      setPersistenceSource,
    } = this.props;
    const { contactsSearchQuery } = this.state;

    return (
      <Modal show={show} className="select-contact-modal-v2" onHide={onCancel} dialogClassName="modal-dialog-centered">
        <Modal.Body className="d-flex flex-column">
          <ContactsTable
            selectable={false}
            contacts={contacts}
            title={layout === 'Client' ? formatMessage('SelectContactModal.ClientTitle') : formatMessage('SelectContactModal.ContactTitle')}
            onClick={onSelect}
            user={user}
            layout={layout}
            hasMore={nextContactsToken != null}
            onLoadMore={this.onLoadMoreContacts}
            onSortingChange={this.onContactsSortingChange}
            onContactsSearch={this.onContactsSearchChange}
            debouncedSearchContacts={this.debouncedSearchContacts}
            searchQuery={contactsSearchQuery}
            loading={loading}
            persistenceSource={persistenceSource}
            orderedFields={[ContactsTableFields.Fname, ContactsTableFields.Lname, ContactsTableFields.Email]}
            showHeaderSection
            serverSideSorting
            onServerSideSorting={this.handleSorting}
            sortedColumns={this.state.sortedColumns}
            isPostMessageActive={isPostMessageActive}
            setPersistenceSource={setPersistenceSource}
            setIsActiveContactPostMessage={setIsActiveContactPostMessage}
          />

          <div className="modal-actions d-flex justify-content-end">
            <CancelButton onClick={onCancel} />
          </div>
        </Modal.Body>
      </Modal>
    );
  }
}

const mapStateToProps = (state: ReduxState): StateProps => ({
  contacts: state.contacts.contacts,
  persistenceSource: state.contacts.persistenceSource,
  nextContactsToken: state.contacts.nextContactsToken,
  loading: selectIsLoadingEvery(state, [GET_CONTACTS.type]),
  isPostMessageActive: state.postMessage.isPostMessageActive,
});

export default connect(mapStateToProps, {
  getMyContacts,
  startLoaderAction,
  setIsActiveContactPostMessage,
  setPersistenceSource,
})(SelectContactModal);
